import { getCheckpoint } from '../data/checkpoints';
import type { CheckpointRecord, TrinRecord, UserProfile } from '../types';
import { firstTrinIdInTier } from './curriculum';

const SPRINT_TRIN_TO_CHECKPOINT: Record<string, string> = {
  'grundniveau-trin-7': 'checkpoint-after-grundniveau',
  'niveau-1-trin-16': 'checkpoint-after-niveau-4',
};

const LEGACY_SPRINT_TRIN_IDS = new Set(Object.keys(SPRINT_TRIN_TO_CHECKPOINT));

function trinRecordToCheckpointRecord(
  rec: (TrinRecord & { bestWords?: number }) | undefined,
): CheckpointRecord | undefined {
  if (!rec) return undefined;
  const bestWords = rec.bestWords ?? 0;
  if (rec.attempts === 0 && bestWords === 0) return undefined;
  return {
    bestWords,
    attempts: rec.attempts,
    lastPlayedAt: rec.lastPlayedAt,
  };
}

/** Migrate V3 user profile to V3.1 (checkpoints, no sprint trins). */
export function migrateUserToV31(user: UserProfile): UserProfile {
  let checkpointRecords: Record<string, CheckpointRecord> = {
    ...(user.checkpointRecords ?? {}),
  };
  const trinRecords = { ...(user.trinRecords ?? {}) };

  for (const [trinId, checkpointId] of Object.entries(SPRINT_TRIN_TO_CHECKPOINT)) {
    const legacy = trinRecords[trinId];
    if (legacy) {
      const migrated = trinRecordToCheckpointRecord(legacy);
      if (migrated) {
        const existing = checkpointRecords[checkpointId];
        checkpointRecords[checkpointId] = existing
          ? {
              bestWords: Math.max(existing.bestWords, migrated.bestWords),
              attempts: existing.attempts + migrated.attempts,
              lastPlayedAt:
                migrated.lastPlayedAt > existing.lastPlayedAt
                  ? migrated.lastPlayedAt
                  : existing.lastPlayedAt,
            }
          : migrated;
      }
      delete trinRecords[trinId];
    }
  }

  let completedTrinIds = user.completedTrinIds.filter(
    (id) => !LEGACY_SPRINT_TRIN_IDS.has(id),
  );

  let currentTrinId = user.currentTrinId;
  if (LEGACY_SPRINT_TRIN_IDS.has(currentTrinId)) {
    const checkpointId = SPRINT_TRIN_TO_CHECKPOINT[currentTrinId];
    const cp = checkpointId ? getCheckpoint(checkpointId) : undefined;
    currentTrinId =
      (cp && firstTrinIdInTier(cp.beforeTierId)) ?? firstTrinIdInTier('niveau-1') ?? currentTrinId;
  }

  return {
    ...user,
    role: user.role ?? 'learner',
    checkpointRecords,
    trinRecords,
    completedTrinIds,
    currentTrinId,
  };
}
